import { jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { Box, FormField, Combobox, FormHelp, Label } from "@zillow/constellation-10";
import PropertySearch from "@zillow/property-search";
import { useFormContext, useWatch } from "react-hook-form";
import { useBlockEnvironment } from "../../contexts/BlockEnvironment/BlockEnvironmentProvider.esm.js";
const ADDRESS_FORM_NAME = "address";
const PROPERTY_SEARCH_CONFIG_VALUES = {
  prod: {
    addressAutocompleteURL: "https://www.zillowstatic.com/autocomplete/v3/suggestions/"
  },
  test: {
    addressAutocompleteURL: "https://static.qa.zillow.net/autocomplete/v3/suggestions/"
  }
};
const TRIGGER = '& [data-c11n-component="Combobox.Trigger"]';
const CLEAR_BUTTON = '[data-c11n-component="Combobox.Clear"]';
const addressFieldSizeStyles = css.raw({
  [TRIGGER]: {
    boxSizing: "border-box",
    minHeight: "{spacing.1100}",
    paddingBlock: "calc({spacing.200} + {borderWidths.default})",
    paddingInline: "{spacing.400}"
  },
  // The input group reserves room for multi-select chips (28-32px); this field has none
  '& [data-c11n-component="Combobox.InputGroup"]': {
    minHeight: "auto"
  }
});
const addressFieldSurfaceStyles = css.raw({
  [TRIGGER]: {
    backgroundColor: "gray.50",
    boxShadowColor: "gray.300",
    _hover: {
      backgroundColor: "gray.50",
      boxShadowColor: "blue.600"
    },
    "&:has(input:focus)": {
      backgroundColor: "Input.bg.active",
      boxShadowColor: "blue.600"
    }
  }
});
const addressInputStyles = css.raw({
  color: "text.neutral",
  _placeholder: {
    // Slightly darker than `Input.text.placeholder` for readability (aligns with prod).
    color: "gray.600"
  }
});
const renderNoAdornment = () => null;
const AddressAutocompleteField = ({
  onSelect,
  placeholder = "Enter the complete property address",
  label = "Property Address",
  description,
  required = false,
  error,
  onBlur: addressOnBlur,
  inputCssExtra,
  formHelpCss
}) => {
  var _a;
  const {
    environment = "prod"
  } = useBlockEnvironment();
  const {
    control
  } = useFormContext();
  const addressForDisplay = String(useWatch({
    control,
    name: ADDRESS_FORM_NAME,
    defaultValue: ""
  }) ?? "");
  const hasError = Boolean(error);
  const handleSubmit = (selection) => {
    const selectedAddress = (selection == null ? void 0 : selection.address) || addressForDisplay;
    const zpid = (selection == null ? void 0 : selection.zpid) ? String(selection.zpid) : void 0;
    if (selectedAddress) {
      onSelect(selectedAddress, zpid);
    }
  };
  const handleClearClick = (event) => {
    if (event.target.closest(CLEAR_BUTTON)) {
      onSelect("");
    }
  };
  return /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(FormField, { error: hasError, label: /* @__PURE__ */ jsx(Label, { css: {
    paddingBottom: "spacing.50",
    fontWeight: "fontWeights.bold",
    color: "text.neutral"
  }, children: label }), required, description: /* @__PURE__ */ jsx(FormHelp, { error: hasError, ...formHelpCss ? {
    css: formHelpCss
  } : {}, children: error || description }), control: /* @__PURE__ */ jsx(Box, { css: css.raw({
    width: "100%"
  }, addressFieldSizeStyles, !hasError && addressFieldSurfaceStyles), onClickCapture: handleClearClick, children: /* @__PURE__ */ jsx(
    PropertySearch,
    {
      renderEmptyState: null,
      defaultAddressValue: addressForDisplay,
      renderInput: ({
        options: _options,
        renderEmptyState: _renderEmptyState,
        ...props
      }) => /* @__PURE__ */ jsx(Combobox.Input, { ...props, placeholder, required, onInput: (e) => {
        onSelect(e.currentTarget.value);
      }, onBlurCapture: () => {
        addressOnBlur == null ? void 0 : addressOnBlur();
      }, css: css.raw(addressInputStyles, inputCssExtra) }),
      renderAdornment: renderNoAdornment,
      addressAutocompleteUrl: ((_a = PROPERTY_SEARCH_CONFIG_VALUES == null ? void 0 : PROPERTY_SEARCH_CONFIG_VALUES[environment]) == null ? void 0 : _a.addressAutocompleteURL) || PROPERTY_SEARCH_CONFIG_VALUES.prod.addressAutocompleteURL,
      onSubmit: handleSubmit
    }
  ) }) }) });
};
export {
  AddressAutocompleteField as default
};
